<!-- src/views/panelMantenimiento/Components/MantenimientoCard.vue -->
<template>
  <article class="card">
    <header class="head">
      <h2 class="title">Mantenimiento (ventana programada)</h2>
      <p class="desc">
        Durante el mantenimiento se bloquearán todas las rutas excepto Login y Panel de mantenimiento.
      </p>
    </header>

    <div class="row">
      <label class="check">
        <input type="checkbox" v-model="enabled" />
        <span>Activar ventana</span>
      </label>
    </div>

    <div class="grid">
      <div class="field">
        <label>Inicio</label>
        <input type="datetime-local" v-model="startsLocal" />
      </div>
      <div class="field">
        <label>Fin</label>
        <input type="datetime-local" v-model="endsLocal" />
      </div>
    </div>

    <div class="field">
      <label>Mensaje (opcional)</label>
      <textarea v-model="message" rows="3" placeholder="Ej: Volvemos en ~20 min"></textarea>
    </div>

    <div class="actions">
      <button class="btn" :disabled="saving" @click="save">
        {{ saving ? 'Guardando…' : 'Guardar programación' }}
      </button>

      <span v-if="statusText" class="hint">{{ statusText }}</span>
    </div>

    <div v-if="status" class="status">
      <div v-if="status.active">Estado: <strong class="bad">ACTIVO</strong></div>
      <div v-else-if="status.upcoming">Estado: <strong class="warn">PROGRAMADO</strong></div>
      <div v-else>Estado: <strong class="ok">SIN MANTENIMIENTO</strong></div>

      <div v-if="status.startsAt">Inicio: {{ fmtMx(status.startsAt) }}</div>
      <div v-if="status.endsAt">Fin: {{ fmtMx(status.endsAt) }}</div>
    </div>
  </article>
</template>

<script>
import { MaintenanceService } from '../Services/MaintenanceService';

function pad(n) { return String(n).padStart(2, '0'); }

// Convierte formatos típicos del backend a ISO UTC parseable por JS
function coerceUtcIso(v) {
  if (!v) return v;
  if (typeof v !== 'string') return v;

  let s = v.trim();

  // "YYYY-MM-DD HH:mm:ss" -> "YYYY-MM-DDTHH:mm:ssZ"
  if (/^\d{4}-\d{2}-\d{2}\s+\d{2}:\d{2}(:\d{2})?$/.test(s)) {
    s = s.replace(/\s+/, 'T') + 'Z';
    return s;
  }

  // "YYYY-MM-DDTHH:mm" o "YYYY-MM-DDTHH:mm:ss" SIN zona -> asume UTC y agrega Z
const hasTz = /([zZ]|[+-]\d{2}:\d{2})$/.test(s);
  if (!hasTz && /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(s)) {
    s = s + 'Z';
  }

  return s;
}

// ISO (UTC) -> "YYYY-MM-DDTHH:mm" local para datetime-local
function isoToLocalInput(iso) {
  if (!iso) return '';
  const d = new Date(coerceUtcIso(iso));
  if (Number.isNaN(d.getTime())) return '';

  const y = d.getFullYear();
  const m = pad(d.getMonth() + 1);
  const day = pad(d.getDate());
  const hh = pad(d.getHours());
  const mm = pad(d.getMinutes());
  return `${y}-${m}-${day}T${hh}:${mm}`;
}


// "YYYY-MM-DDTHH:mm" (local) -> UTC ISO "....Z"
function localInputToUtcIso(localStr) {
  if (!localStr) return null;
  const d = new Date(localStr); // interpretado en zona local del navegador
  if (Number.isNaN(d.getTime())) return null;
  return d.toISOString();
}

export default {
  name: 'MantenimientoCard',
  data() {
    return {
      enabled: false,
      startsLocal: '',
      endsLocal: '',
      message: '',
      saving: false,
      status: null,
      statusText: ''
    };
  },
  methods: {
    fmtMx(iso) {
      try {
        return new Date(iso).toLocaleString('es-MX', { timeZone: 'America/Mexico_City' });
      } catch {
        return new Date(iso).toLocaleString();
      }
    },

    async load() {
      const full = await MaintenanceService.getFull();
      const row = full?.maintenance || {};

      this.enabled = !!(row.is_enabled ?? row.enabled);

      // backend normalmente trae UTC ISO en starts_at / ends_at
      const startsAt = row.starts_at ?? row.startsAt ?? null;
      const endsAt = row.ends_at ?? row.endsAt ?? null;

      this.startsLocal = isoToLocalInput(startsAt);
      this.endsLocal = isoToLocalInput(endsAt);
      this.message = row.message || '';

      this.status = await MaintenanceService.getStatus({ force: true });
      this.statusText = this.status.active
        ? 'En mantenimiento'
        : this.status.upcoming ? 'Programado' : 'Sin mantenimiento';
    },

    async save() {
      this.saving = true;
      try {
        const payload = {
          is_enabled: this.enabled,
          starts_at: localInputToUtcIso(this.startsLocal),
          ends_at: localInputToUtcIso(this.endsLocal),
          message: this.message?.trim() ? this.message.trim() : null
        };

        if (payload.is_enabled) {
          if (!payload.starts_at || !payload.ends_at) {
            throw new Error('Define inicio y fin para activar la ventana.');
          }
        }

        await MaintenanceService.schedule(payload);

        // toast (si existe global)
        try { window?.$toast?.success?.('Programación guardada'); } catch { /* noop */ }

        await this.load();
      } catch (e) {
        try { window?.$toast?.error?.(e?.message || 'Error al guardar'); } catch { /* noop */ }
      } finally {
        this.saving = false;
      }
    }
  },
  mounted() {
    this.load().catch(() => {
      try { window?.$toast?.error?.('No se pudo cargar mantenimiento'); } catch { /* noop */ }
    });
  }
};
</script>

<style lang="sass" scoped>
.card
  text-align: left
  border-radius: 16px
  padding: 16px
  border: 1px solid rgba(15, 23, 42, .10)
  background: #ffffff
  box-shadow: 0 10px 30px rgba(2, 6, 23, .08)
  color: #0f172a

.head
  margin-bottom: 12px

.title
  margin: 0
  font-weight: 900
  font-size: 1.05rem
  color: #0f172a

.desc
  margin: 6px 0 0
  font-size: .9rem
  color: #475569
  font-weight: 600

.row
  margin: 10px 0 12px

.check
  display: inline-flex
  align-items: center
  gap: .5rem
  font-weight: 800
  color: #0f172a
  user-select: none
  input
    transform: translateY(1px)

.grid
  display: grid
  grid-template-columns: 1fr
  gap: 10px
  margin-bottom: 10px
  @media (min-width: 768px)
    grid-template-columns: 1fr 1fr

.field
  display: grid
  gap: 6px
  label
    font-size: .82rem
    color: #334155
    font-weight: 800

input, textarea
  width: 100%
  border-radius: 12px
  border: 1px solid rgba(15, 23, 42, .14)
  background: #ffffff
  color: #0f172a
  padding: 10px 12px
  outline: none
  box-shadow: inset 0 1px 0 rgba(2, 6, 23, .03)
  &::placeholder
    color: #94a3b8
  &:focus
    border-color: rgba(37, 99, 235, .55)
    box-shadow: 0 0 0 4px rgba(37, 99, 235, .12)

.actions
  display: flex
  align-items: center
  gap: 12px
  margin-top: 10px
  flex-wrap: wrap

.btn
  border-radius: 999px
  border: 1px solid rgba(37, 99, 235, .25)
  background: #2563eb
  color: #ffffff
  padding: 10px 14px
  font-weight: 900
  cursor: pointer
  transition: transform .05s ease, filter .15s ease, box-shadow .15s ease
  box-shadow: 0 10px 20px rgba(37, 99, 235, .18)
  &:hover
    filter: brightness(1.03)
  &:active
    transform: translateY(1px)
  &:disabled
    opacity: .65
    cursor: not-allowed
    box-shadow: none

.hint
  font-size: .9rem
  color: #475569
  font-weight: 700

.status
  margin-top: 12px
  font-size: .9rem
  color: #334155
  display: grid
  gap: 4px

.bad
  color: #b91c1c

.warn
  color: #b45309

.ok
  color: #15803d
</style>